import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Typography,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material"; // List/ListItem/ListItemText used in dialog
import {
  CloudUpload,
  Error as ErrorIcon,
  Visibility,
  Close,
} from "@mui/icons-material";
import {
  createUploadSession,
  uploadImageChunk,
  getUploadSessionStatus,
  reportSkippedFiles,
} from "../../../api/images";
import { BatchStats, Job } from "../../../pages/playground/types";

interface UploadImagesProps {
  batchId: string;
  stats: BatchStats;
  jobs: Job[];
  onUploadComplete: () => void;
}

interface SessionProgress {
  total: number;
  completed: number;
  skipped: number;
  failed: number;
  in_progress: number;
}

interface SessionResults {
  successful_uploads: Array<{
    sku: string;
    filename: string;
    images_added: number;
  }>;
  failed_uploads: Array<{
    filename: string;
    reason: string;
  }>;
  skipped_uploads: Array<{
    filename: string;
    reason: string;
    sku?: string;
  }>;
}

interface SessionStatus {
  session_id: string;
  status: "processing" | "completed";
  progress: SessionProgress;
  results: SessionResults;
}

export const UploadImages: React.FC<UploadImagesProps> = ({
  batchId,
  stats,
  jobs,
  onUploadComplete,
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadingChunks, setUploadingChunks] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [filesUploaded, setFilesUploaded] = useState(0);
  const [totalFiles, setTotalFiles] = useState(0);
  const [frontendSkippedFiles, setFrontendSkippedFiles] = useState<string[]>(
    []
  );
  const [sessionStatus, setSessionStatus] = useState<SessionStatus | null>(
    null
  );
  const [dragActive, setDragActive] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  const jobsWithoutImagesCount = stats?.jobs_without_images || 0;

  // Filter jobs to get those without images
  const jobsWithoutImages = jobs.filter(
    (job) => job.missing_images || (!job.images && !job.thumbnails)
  );

  // Split array into chunks
  const chunkArray = <T,>(array: T[], size: number): T[][] => {
    const chunks: T[][] = [];
    for (let i = 0; i < array.length; i += size) {
      chunks.push(array.slice(i, i + size));
    }
    return chunks;
  };

  // Upload files in chunks
  const uploadFiles = async (files: File[]) => {
    try {
      await handleReset();
      setUploadingChunks(true);

      // Create session and get existing images
      const sessionResponse = await createUploadSession({
        batchId,
        estimatedFiles: files.length,
      });

      const sessionId = sessionResponse.data.data.session_id;
      const existingImages = sessionResponse.data.data.existing_images || {};

      // Filter out duplicate files by comparing base filenames (without extensions)
      const skippedFiles: string[] = [];
      const filesToUpload = files.filter((file) => {
        const baseFilename = file.name.replace(/\.[^/.]+$/, ""); // Remove extension

        // Check if this file already exists for any SKU
        for (const sku in existingImages) {
          const existingFilenames = existingImages[sku] || [];
          for (const existingFile of existingFilenames) {
            const existingBase = existingFile.replace(/\.[^/.]+$/, "");
            if (existingBase === baseFilename) {
              skippedFiles.push(file.name); // Track skipped filename
              return false; // Skip this file
            }
          }
        }
        return true; // Keep this file
      });

      // Report skipped files to backend to adjust total_files count
      if (skippedFiles.length > 0) {
        setFrontendSkippedFiles(skippedFiles);
        await reportSkippedFiles({
          sessionId: sessionId,
          skippedCount: skippedFiles.length,
        });
      }

      if (filesToUpload.length === 0) {
        alert("All selected files already exist. No files to upload.");
        setUploadingChunks(false);
        setUploading(false);
        return;
      }

      setTotalFiles(filesToUpload.length);
      setFilesUploaded(0);

      // Start polling immediately
      setUploading(true);
      pollSessionStatus(sessionId);

      // Split files into chunks of 10
      const chunks = chunkArray(filesToUpload, 10);

      let uploadedSoFar = 0;

      // Upload each chunk sequentially
      for (let i = 0; i < chunks.length; i++) {
        await uploadImageChunk({
          sessionId: sessionId,
          files: chunks[i],
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              // Calculate progress for current file within chunk
              const currentFileProgress =
                progressEvent.loaded / progressEvent.total;

              // Total progress = files already uploaded + progress of current file
              const totalProgress = uploadedSoFar + currentFileProgress;
              setFilesUploaded(Math.floor(totalProgress));

              // Calculate percentage
              const percentCompleted = Math.round(
                (totalProgress / filesToUpload.length) * 100
              );
              setUploadProgress(percentCompleted);
            }
          },
        });

        // Update files uploaded after chunk completes
        uploadedSoFar += chunks[i].length;
        setFilesUploaded(uploadedSoFar);
      }

      // All chunks uploaded
      setUploadingChunks(false);
    } catch (error) {
      console.error("Upload failed:", error);
      setUploadingChunks(false);
      setUploading(false);
    }
  };

  // Poll session status (first poll is immediate, subsequent polls wait 3s after previous completes)
  const pollSessionStatus = async (sid: string) => {
    try {
      // Make request immediately
      const response = await getUploadSessionStatus({ sessionId: sid });
      const data: SessionStatus = response.data.data;

      setSessionStatus(data);

      if (data.status === "completed") {
        setUploading(false);
        onUploadComplete();
      } else {
        // Schedule next poll after 3 seconds, ensuring previous request completed
        setTimeout(() => pollSessionStatus(sid), 3000);
      }
    } catch (error) {
      console.error("Failed to poll status:", error);
      setUploading(false);
    }
  };

  // Handle drag events
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  // Handle drop
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const files = Array.from(e.dataTransfer.files);
      const validExtensions = [".jpg", ".jpeg", ".png", ".webp", ".zip"];
      const validFiles = files.filter((file) => {
        const ext = "." + file.name.split(".").pop()?.toLowerCase();
        return validExtensions.includes(ext);
      });

      if (validFiles.length > 0) {
        uploadFiles(validFiles);
      }
    }
  };

  // Handle file input
  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const files = Array.from(e.target.files);
      uploadFiles(files);
    }
  };

  // Reset to initial state
  const handleReset = () => {
    setSessionStatus(null);
    setUploading(false);
    setUploadingChunks(false);
    setUploadProgress(0);
    setFilesUploaded(0);
    setTotalFiles(0);
    setFrontendSkippedFiles([]);
  };

  return (
    <Card>
      <CardContent>
        {/* Stats Header */}
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Typography variant="h6">Upload Images</Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Visibility />}
              onClick={() => setDetailDialogOpen(true)}
            >
              {jobsWithoutImagesCount} SKUs Without Images
            </Button>
          </Box>
        </Box>

        {/* Upload Area */}
        {!uploadingChunks && !uploading && !sessionStatus && (
          <Box
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            sx={{
              border: "2px dashed",
              borderColor: dragActive ? "primary.main" : "grey.300",
              borderRadius: 2,
              p: 4,
              textAlign: "center",
              cursor: "pointer",
              bgcolor: dragActive ? "action.hover" : "background.paper",
              transition: "all 0.3s",
              position: "relative",
            }}
            onClick={() =>
              document.getElementById("file-upload-input")?.click()
            }
          >
            <input
              id="file-upload-input"
              type="file"
              multiple
              accept=".jpg,.jpeg,.png,.webp,.zip"
              onChange={handleFileInput}
              style={{ display: "none" }}
            />
            <CloudUpload sx={{ fontSize: 48, color: "primary.main", mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {dragActive
                ? "Drop files here..."
                : "Drag & drop one or more images or zip files"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Supported: JPG, JPEG, PNG, WEBP, ZIP (max 256MB per file)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Images are matched to SKUs by filename prefix (e.g.,
              SKU123_front.jpg → SKU123)
            </Typography>
          </Box>
        )}

        {/* Upload and Processing Progress */}
        {(uploadingChunks || uploading) && (
          <Box sx={{ mt: 2 }}>
            {/* Processing Progress */}
            {uploading && sessionStatus && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" gutterBottom>
                  Processing{" "}
                  {sessionStatus.progress.completed +
                    sessionStatus.progress.skipped}{" "}
                  of {sessionStatus.progress.total} images...
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={
                    sessionStatus.progress.total > 0
                      ? ((sessionStatus.progress.completed +
                          sessionStatus.progress.skipped) /
                          sessionStatus.progress.total) *
                        100
                      : 0
                  }
                />
              </Box>
            )}

            {/* Upload Progress */}
            {uploadingChunks && (
              <Box>
                <Typography variant="body2" gutterBottom>
                  Uploading... {filesUploaded} of {totalFiles} ({uploadProgress}
                  %)
                </Typography>
                <LinearProgress variant="determinate" value={uploadProgress} />
              </Box>
            )}
          </Box>
        )}

        {/* Results */}
        {sessionStatus && sessionStatus.status === "completed" && (
          <Box sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Summary:</strong> {sessionStatus.progress.completed}{" "}
                uploaded,{" "}
                {sessionStatus.progress.skipped + frontendSkippedFiles.length}{" "}
                skipped,{" "}
                {sessionStatus.progress.failed} failed
              </Typography>
            </Alert>

            {/* Scrollable Results Container */}
            <Box sx={{ maxHeight: 400, overflow: "auto", mb: 2 }}>
              {/* Successful Uploads */}
              {sessionStatus.results.successful_uploads.length > 0 && (
                <Box sx={{ mb: 1.5 }}>
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    sx={{ display: "flex", alignItems: "center", mb: 0.5 }}
                  >
                    <Box
                      component="span"
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: "success.main",
                        mr: 1,
                      }}
                    />
                    Successful Uploads (
                    {sessionStatus.results.successful_uploads.length})
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 0.25,
                      bgcolor: "#e8f5e9",
                      borderRadius: 1,
                      px: 0.75,
                      pt: 0.5,
                      pb: 0.25,
                    }}
                  >
                    {sessionStatus.results.successful_uploads.map(
                      (upload, idx) => (
                        <Typography
                          key={idx}
                          variant="body2"
                          noWrap
                          title={`${upload.filename} • ${upload.sku}`}
                          sx={{ minWidth: 0 }}
                        >
                          {upload.filename}
                        </Typography>
                      )
                    )}
                  </Box>
                </Box>
              )}

              {/* Skipped Uploads */}
              {(sessionStatus.results.skipped_uploads.length > 0 ||
                frontendSkippedFiles.length > 0) && (
                <Box sx={{ mb: 1.5 }}>
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    sx={{ display: "flex", alignItems: "center", mb: 0.5 }}
                  >
                    <Box
                      component="span"
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: "warning.main",
                        mr: 1,
                      }}
                    />
                    Skipped Uploads (
                    {sessionStatus.results.skipped_uploads.length +
                      frontendSkippedFiles.length}
                    ) —{" "}
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.secondary"
                      sx={{ ml: 0.5 }}
                    >
                      duplicate or already uploaded
                    </Typography>
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 0.25,
                      bgcolor: "#fff8e1",
                      borderRadius: 1,
                      px: 0.75,
                      pt: 0.5,
                      pb: 0.25,
                    }}
                  >
                    {/* Frontend skipped files (already exist) */}
                    {frontendSkippedFiles.map((filename, idx) => (
                      <Typography
                        key={`frontend-${idx}`}
                        variant="body2"
                        noWrap
                        title={`${filename} • Already exists`}
                        sx={{ minWidth: 0 }}
                      >
                        {filename}
                      </Typography>
                    ))}
                    {/* Backend skipped files */}
                    {sessionStatus.results.skipped_uploads.map(
                      (upload, idx) => (
                        <Typography
                          key={`backend-${idx}`}
                          variant="body2"
                          noWrap
                          title={`${upload.filename} • ${upload.reason}`}
                          sx={{ minWidth: 0 }}
                        >
                          {upload.filename}
                        </Typography>
                      )
                    )}
                  </Box>
                </Box>
              )}

              {/* Failed Uploads */}
              {sessionStatus.results.failed_uploads.length > 0 && (
                <Box sx={{ mb: 1.5 }}>
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    sx={{ display: "flex", alignItems: "center", mb: 0.5 }}
                  >
                    <ErrorIcon
                      sx={{ fontSize: 16, color: "error.main", mr: 1 }}
                    />
                    Failed Uploads ({sessionStatus.results.failed_uploads.length}
                    ) —{" "}
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.secondary"
                      sx={{ ml: 0.5 }}
                    >
                      no matching SKU, invalid format, or corrupted file
                    </Typography>
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 0.25,
                      bgcolor: "#ffebee",
                      borderRadius: 1,
                      px: 0.75,
                      pt: 0.5,
                      pb: 0.25,
                    }}
                  >
                    {sessionStatus.results.failed_uploads.map((upload, idx) => (
                      <Typography
                        key={idx}
                        variant="body2"
                        noWrap
                        title={`${upload.filename} • ${upload.reason}`}
                        sx={{ minWidth: 0 }}
                      >
                        {upload.filename}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>

            {/* Reset Button */}
            <Box sx={{ mt: 2 }}>
              <Button variant="outlined" onClick={handleReset}>
                Upload More Images
              </Button>
            </Box>
          </Box>
        )}
      </CardContent>

      {/* Detailed View Dialog for SKUs without Images */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <ErrorIcon sx={{ color: "warning.main" }} />
              <Typography variant="h6">
                {jobsWithoutImages.length} SKUs Without Images
              </Typography>
            </Box>
            <IconButton
              onClick={() => setDetailDialogOpen(false)}
              size="small"
              aria-label="close"
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {jobsWithoutImages.length > 0 ? (
            <Box sx={{ maxHeight: 400, overflow: "auto" }}>
              <List dense>
                {jobsWithoutImages.map((job) => (
                  <ListItem key={job.id}>
                    <ListItemText
                      primary={
                        <Typography variant="body1" fontWeight="medium">
                          {job.master_id}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              All jobs have images.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDetailDialogOpen(false)}
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};
